// src/pages/NearbyArtworksPage.js
import React, { useEffect, useState } from 'react';
import { http } from "../lib/axios";
import { Container, Typography, Grid, CircularProgress } from '@mui/material';
import ArtworkCard from '../components/ArtworkCard';
import { useLocation } from 'react-router-dom';
import HostOfferModal from "../components/HostOfferModal";
import { createHostOffer, listMyArtworkAgreements } from "../lib/apiHostingAgreements";

export default function NearbyArtworksPage() {
  const location = useLocation();
  const passedDistance = location.state?.distance || 20;
  const [artworks, setArtworks] = useState([]);
  const [requestedArtworkIds, setRequestedArtworkIds] = useState([]);
  const [offerModalOpen, setOfferModalOpen] = useState(false);
  const [offerArtwork, setOfferArtwork] = useState(null);
  const [loadingOffers, setLoadingOffers] = useState(false);
  const [offerSubmitting, setOfferSubmitting] = useState(false);
  const [hostId, setHostId] = useState(null);

  useEffect(() => {
    const fetchNearbyArtworks = async () => {
      try {
        const {data} = await http.get('/api/accounts/me/');
        const userId = data.id;

        const hostRes = await http.get(`/hosts/?user=${userId}`);
        if (!hostRes.data.length) return;

        const hostProfile = hostRes.data[0];
        setHostId(hostProfile?.id || null);
        const { latitude, longitude } = hostProfile.location;

        const artworksRes = await http.get(
          `/artworks/nearby/?lat=${latitude}&lon=${longitude}&radius=${passedDistance}&view=card`
        );
        setArtworks(artworksRes.data);

        setLoadingOffers(true);
        const agreementsRes = await listMyArtworkAgreements();
        const agreements = Array.isArray(agreementsRes.data)
          ? agreementsRes.data
          : agreementsRes.data?.results || [];
        const hostFiltered = hostProfile?.id
          ? agreements.filter(item => (item.host?.id || item.host) === hostProfile.id)
          : agreements;
        const artworkIds = hostFiltered
          .filter(item => String(item?.status || "").includes("PENDING"))
          .map(item => item?.artwork?.id || item?.artwork_id || item?.artwork)
          .filter(Boolean);
        setRequestedArtworkIds(artworkIds);
      } catch (err) {
        console.error('Failed to load nearby artworks:', err);
      } finally {
        setLoadingOffers(false);
      }
    };
    fetchNearbyArtworks();
  }, [passedDistance]);

  const handleOfferClick = (artwork) => {
    setOfferArtwork(artwork);
    setOfferModalOpen(true);
  };

  const handleOfferSubmit = async (terms) => {
    if (!offerArtwork?.id) return;
    try {
      setOfferSubmitting(true);
      await createHostOffer(offerArtwork.id, terms, { hostId });
      setOfferModalOpen(false);
      setRequestedArtworkIds((prev) => [...prev, offerArtwork.id]);
    } finally {
      setOfferSubmitting(false);
    }
  };

  return (
    <Container sx={{ mt: 5 }}>
      <Typography variant="h4" gutterBottom>Nearby Artworks</Typography>
      {loadingOffers && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          <CircularProgress size={16} sx={{ mr: 1 }} />
          Loading pending offers...
        </Typography>
      )}
      <Grid container spacing={3}>
        {artworks.map(art => (
          <Grid item xs={12} md={6} lg={4} key={art.id}>
            <ArtworkCard
              art={art}
              showActions={true}
              customActionButton={{
                label: requestedArtworkIds.includes(art.id) ? 'Offer Pending' : 'Request to Host',
                onClick: () => handleOfferClick(art),
                disabled: requestedArtworkIds.includes(art.id)
              }}
            />
          </Grid>
        ))}
      </Grid>

      <HostOfferModal
        open={offerModalOpen}
        onClose={() => setOfferModalOpen(false)}
        artwork={offerArtwork}
        onSubmit={handleOfferSubmit}
        submitting={offerSubmitting}
      />
    </Container>
  );
}
